import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search, ShieldAlert, BookOpen, GraduationCap,
  BadgeCheck, Eye, Users, Scale, LineChart, FileText,
  ArrowRight, CheckCircle2, ChevronRight,
} from "lucide-react";
import { HERO, STATS, QUICK_LINKS, FUNCTIONS, NEWS, IMAGES, ORG } from "../mock";

const ICONS = {
  Search, ShieldAlert, BookOpen, GraduationCap,
  BadgeCheck, Eye, Users, Scale, LineChart, FileText,
};

export default function Home() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {HERO.title}
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed mb-8 max-w-2xl">
              {HERO.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={HERO.primaryCta.path}
                className="inline-flex items-center gap-2 bg-brand-green text-white font-semibold px-6 py-3.5 rounded-md hover:bg-[#0f6339] transition-colors"
              >
                <Search size={18} /> {HERO.primaryCta.label}
              </Link>
              <Link
                to={HERO.secondaryCta.path}
                className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3.5 rounded-md hover:bg-white/20 transition-colors"
              >
                {HERO.secondaryCta.label} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="stat-card bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
                <div className="font-serif text-3xl sm:text-4xl font-bold text-brand-navy">{s.value}</div>
                <div className="text-sm text-slate-500 mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-navy mb-3">How can we help you?</h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {QUICK_LINKS.map((q) => {
              const Icon = ICONS[q.icon];
              return (
                <Link key={q.title} to={q.path} className="quick-link-card group bg-white rounded-xl border border-slate-200 p-7 block">
                  <div className="w-12 h-12 rounded-lg bg-[#eaf4ee] flex items-center justify-center mb-5">
                    <Icon className="text-brand-green" size={24} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-brand-navy mb-2">{q.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4">{q.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-green">
                    Learn more <ChevronRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-sm font-semibold text-brand-green uppercase tracking-wide">About the Commission</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-navy mt-3 mb-5">
              The statutory regulator of Sierra Leone&apos;s securities market
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              {ORG.shortName} is responsible for licensing and supervising securities
              exchanges, brokers, dealers, advisers and collective investment schemes.
              We work to maintain fair, orderly and transparent markets, and to protect
              the investing public.
            </p>
            <ul className="space-y-3 mb-7">
              {[
                "Independent statutory authority",
                "Licensing and supervision of market intermediaries",
                "Enforcement against fraud and market abuse",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-slate-700">
                  <CheckCircle2 size={20} className="text-brand-green shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/about" className="inline-flex items-center gap-2 text-brand-navy font-semibold hover:gap-3 transition-all">
              Read more about us <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative">
            <img src={IMAGES.about} alt="Commission officials" className="rounded-2xl shadow-xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-brand-green text-white rounded-xl p-6 shadow-lg hidden sm:block">
              <div className="font-serif text-3xl font-bold">20+</div>
              <div className="text-sm text-green-50">years protecting investors</div>
            </div>
          </div>
        </div>
      </section>

      {/* Functions */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-brand-green uppercase tracking-wide">Our Mandate</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-navy mt-3 mb-3">What we do</h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FUNCTIONS.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-7 hover:border-brand-green hover:shadow-md transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-lg bg-brand-navy flex items-center justify-center">
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-brand-navy">{f.title}</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* News preview */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sm font-semibold text-brand-green uppercase tracking-wide">Newsroom</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand-navy mt-3">News &amp; public notices</h2>
            </div>
            <Link to="/news" className="hidden sm:inline-flex items-center gap-2 text-brand-navy font-semibold hover:gap-3 transition-all">
              View all <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {NEWS.slice(0, 3).map((n) => (
              <Link key={n.id} to="/news" className="news-card bg-white rounded-xl border border-slate-200 overflow-hidden block">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-semibold text-brand-green bg-[#eaf4ee] px-2.5 py-1 rounded">{n.tag}</span>
                    <span className="text-xs text-slate-400">{n.date}</span>
                  </div>
                  <h3 className="font-serif text-lg font-bold text-brand-navy mb-2 leading-snug">{n.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{n.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
            Before you invest, verify.
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Always confirm that a firm is licensed by {ORG.shortName} and currently active before committing your money.
          </p>
          <Link to="/regulated-entities" className="inline-flex items-center gap-2 bg-brand-green text-white font-semibold px-7 py-3.5 rounded-md hover:bg-[#0f6339] transition-colors">
            <Search size={18} /> Search the register
          </Link>
        </div>
      </section>
    </div>
  );
}
